/**
 * The part every step shares (2.J deliverable 11): Preview, the preview table, Apply and the
 * Applied list. A step's own fields sit above it, in the step's form.
 *
 * Preview is the form's submit button, and the form's submit handler is the one way a preview
 * starts: a click on Preview and Enter in any field of the step take the same path. A form with
 * several fields and no submit button ignores Enter (the HTML implicit-submission rule), which is
 * why Enter in the count field once did nothing (G8 preview fix).
 */
import { Banner, Button } from '@codex/ui';
import type { ReactNode } from 'react';
import { type Issue, issueText, type StepState } from './model/wizard.js';
import { PreviewTable } from './preview-table.js';

export function IssueList({ issues }: { issues: readonly Issue[] }) {
  if (issues.length === 0) return null;
  return (
    <ul className="cx-bootstrap__issues" role="alert">
      {issues.map((issue) => (
        <li key={`${issue.index ?? 'all'}-${issue.code}`}>
          {issue.index === null ? '' : `Row ${issue.index + 1}: `}
          {issueText(issue.code)}
        </li>
      ))}
    </ul>
  );
}

export function StepPanel({
  state,
  canApply,
  previewDisabled,
  previewNote,
  inlineIssues,
  onApply,
  children,
}: {
  state: StepState;
  canApply: boolean;
  previewDisabled?: boolean;
  /** Why Preview is disabled, when it is. */
  previewNote?: string | undefined;
  /** The step shows its issues at its own rows. */
  inlineIssues?: boolean;
  onApply: () => void;
  /** The Applied list. */
  children?: ReactNode;
}) {
  const busy = state.phase === 'previewing';
  return (
    <div className="cx-bootstrap__panel">
      <div className="cx-bootstrap__actions">
        <Button
          icon="eye"
          type="submit"
          busy={busy}
          disabled={previewDisabled === true || busy || state.phase === 'applying'}
        >
          Preview
        </Button>
        <Button
          variant="primary"
          data-testid="bootstrap.apply.button"
          busy={state.phase === 'applying'}
          disabled={!canApply}
          onClick={onApply}
        >
          Apply
        </Button>
        {previewNote !== undefined && <span className="cx-bootstrap__muted">{previewNote}</span>}
      </div>
      {state.notice !== null && (
        <Banner tone="warning" live="assertive">
          {state.notice}
        </Banner>
      )}
      {inlineIssues !== true && <IssueList issues={state.issues} />}
      {state.phase === 'applied' && (
        <p role="status" className="cx-bootstrap__muted">
          Applied.
        </p>
      )}
      {state.preview !== null && (
        <PreviewTable key={state.preview.previewHash} preview={state.preview} />
      )}
      {children}
    </div>
  );
}
